import {useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {Link, useLocation} from 'react-router-dom';
import {LayoutGrid, Search} from 'lucide-react';
import {cn} from '@/matic/lib/utils/cn';
import {useCanImportPastEmails} from '../hooks/useQuoting';
import {
  QUOTE_RESOURCES,
  RESOURCE_MENU,
  type QuoteResource,
} from './quote-resources';

const MENU_WIDTH = 560;
const VIEWPORT_MARGIN = 8;

type Entry = QuoteResource;

type Group = {heading: string; entries: Entry[]};

/**
 * The Quote Desk's resources (quote-resources.ts) in the desk wireframe's groups. A value's
 * Source names one of these, so the menu and the sources stay one list. Entries without a
 * destination are marked as not built rather than linked to nothing.
 */
const COLUMNS: Group[][] = RESOURCE_MENU.map((column) =>
  column.map((group) => ({
    heading: group.heading,
    entries: group.ids.map((id) => QUOTE_RESOURCES[id]),
  }))
);

const matches = (entry: Entry, query: string): boolean =>
  !query ||
  `${entry.title} ${entry.description}`
    .toLowerCase()
    .includes(query.toLowerCase());

function EntryRow({entry, onNavigate}: {entry: Entry; onNavigate: () => void}) {
  const Icon = entry.icon;
  const body = (
    <>
      <Icon className="row-span-2 mt-0.5 h-4 w-4 shrink-0" />
      <span className="flex items-center gap-1.5 text-[13px] font-semibold leading-tight">
        {entry.title}
        {!entry.to && (
          <span className="rounded-sm bg-neutral-100 px-1 py-px text-[9px] font-medium uppercase tracking-wide text-neutral-500">
            not built
          </span>
        )}
      </span>
      <span className="text-[11.5px] leading-tight text-neutral-500">
        {entry.description}
      </span>
    </>
  );
  const layout =
    'grid grid-cols-[18px_1fr] gap-x-2 gap-y-0.5 rounded-md px-2 py-1.5 text-left';
  return entry.to ? (
    <Link
      to={entry.to}
      onClick={onNavigate}
      className={cn(
        layout,
        'text-neutral-900 no-underline hover:bg-neutral-100'
      )}
    >
      {body}
    </Link>
  ) : (
    <div
      aria-disabled
      title="Not built yet"
      className={cn(layout, 'cursor-default text-neutral-400')}
    >
      {body}
    </div>
  );
}

/**
 * The Resources flyout at the foot of the rail (Ctrl/⌘+K): the precedent explorer, the
 * repositories, the request table, review and admin — one place for everything that is
 * not the queue. Pops out to the right of its trigger; Escape, a click outside or a
 * navigation closes it.
 */
/**
 * `ribbon` puts a compact trigger in the dark ribbon and drops the panel below it,
 * right-aligned; `rail` is the original full-width rail row opening to its right.
 */
export function QuoteResourcesMenu({
  placement = 'rail',
}: {
  placement?: 'rail' | 'ribbon';
}) {
  const inRibbon = placement === 'ribbon';
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [position, setPosition] = useState<{
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
    width: number;
  }>({width: MENU_WIDTH});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const {pathname} = useLocation();
  const canImportEmails = useCanImportPastEmails();

  // A navigation closes it, including one from its own entries.
  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen((v) => !v);
      } else if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    globalThis.addEventListener('keydown', onKey);
    return () => {
      globalThis.removeEventListener('keydown', onKey);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    setQuery('');
    const reposition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      if (inRibbon) {
        setPosition({
          right: Math.max(VIEWPORT_MARGIN, window.innerWidth - rect.right),
          top: rect.bottom + VIEWPORT_MARGIN,
          width: Math.min(MENU_WIDTH, window.innerWidth - 2 * VIEWPORT_MARGIN),
        });
        return;
      }

      const left = rect.right + VIEWPORT_MARGIN;
      setPosition({
        left,
        bottom: Math.max(VIEWPORT_MARGIN, window.innerHeight - rect.bottom),
        width: Math.min(MENU_WIDTH, window.innerWidth - left - VIEWPORT_MARGIN),
      });
    };

    reposition();
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        (panelRef.current?.contains(target) ?? false) ||
        triggerRef.current?.contains(target)
      )
        return;
      setOpen(false);
    };

    window.addEventListener('resize', reposition);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('resize', reposition);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, inRibbon]);

  const close = () => {
    setOpen(false);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          setOpen((v) => !v);
        }}
        className={cn(
          'flex shrink-0 items-center text-left font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
          inRibbon
            ? 'h-7 gap-2 rounded-md px-2.5 text-[13px] hover:bg-sidebar-hover'
            : 'w-full gap-2.5 px-4 py-1.5 text-sm',
          open ? 'text-white' : 'text-sidebar-fg/55 hover:text-white'
        )}
      >
        <LayoutGrid className="h-4 w-4 shrink-0" />
        <span className={inRibbon ? undefined : 'flex-1'}>Resources</span>
        <kbd className="rounded border border-white/15 px-1 font-mono text-[10px] text-sidebar-fg/45">
          ⌘K
        </kbd>
      </button>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Resources"
            style={position}
            className="fixed z-50 rounded-lg border border-neutral-200 bg-white p-3.5 text-neutral-900 shadow-xl"
          >
            <label className="flex items-center gap-2 rounded-md border border-neutral-200 px-2 py-1.5">
              <Search className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
              <input
                autoFocus
                value={query}
                onChange={(event) => {
                  setQuery(event.target.value);
                }}
                placeholder="Jump to a resource, engine or quote number"
                className="min-w-0 flex-1 bg-transparent text-xs outline-none placeholder:text-neutral-400"
              />
            </label>
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3">
              {COLUMNS.map((column, index) => (
                <div key={index} className="flex flex-col gap-3">
                  {column.map((group) => {
                    const entries = group.entries.filter(
                      (entry) =>
                        matches(entry, query) &&
                        (entry.id !== 'import-emails' || canImportEmails)
                    );
                    if (entries.length === 0) return null;
                    return (
                      <div key={group.heading}>
                        <h6 className="mb-0.5 px-2 text-[11px] font-semibold uppercase tracking-wide text-neutral-500">
                          {group.heading}
                        </h6>
                        {entries.map((entry) => (
                          <EntryRow
                            key={entry.title}
                            entry={entry}
                            onNavigate={close}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
